import { Box, Paper } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PanelHeader from '~/components/toolbar/shared/PanelHeader';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';

/** Fallback position when the anchoring toolbar button cannot be measured. */
const DEFAULT_POSITION = { x: 120, y: 80 };

interface Position {
    x: number;
    y: number;
}

interface DraggableToolWindowProps {
    /** Whether the panel is shown (the owning tool is selected). */
    open: boolean;
    title: string;
    /** Fixed window width, px. */
    width: number;
    /** The toolbar button the panel is first placed next to. */
    anchorRef: React.RefObject<HTMLElement | null>;
    onClose: () => void;
    children: React.ReactNode;
}

/**
 * Draggable tool window: placed next to the tool's button on first open,
 * moved by its header, position persisted across re-opens (keep it mounted —
 * drive it with `open`). Portalled because the toolbar's CSS `transform`
 * would otherwise anchor `position: fixed` to the toolbar. For a transient
 * popover that closes on click-away, use {@link FloatingPanel} instead. The
 * operations and compare panels draw this same picture with dnd-kit; this
 * keeps the mouse-drag implementation it was extracted with, and converging
 * the two mechanisms is a follow-up, not this feature's scope.
 */
export default function DraggableToolWindow({
    open,
    title,
    width,
    anchorRef,
    onClose,
    children,
}: DraggableToolWindowProps) {
    const [position, setPosition] = useState<Position | null>(null);
    const dragStartRef = useRef<{
        mouseX: number;
        mouseY: number;
        panelX: number;
        panelY: number;
    } | null>(null);

    // Place the panel next to the toolbar button the first time it opens;
    // `position` is never reset, so it persists across re-opens.
    useEffect(() => {
        if (open && !position) {
            const rect = anchorRef.current?.getBoundingClientRect();
            setPosition(
                rect
                    ? { x: rect.right + 26, y: Math.max(50, rect.top) }
                    : DEFAULT_POSITION
            );
        }
    }, [open, position, anchorRef]);

    const handleDragMove = useCallback((e: MouseEvent) => {
        const start = dragStartRef.current;
        if (!start) {
            return;
        }
        setPosition({
            x: start.panelX + (e.clientX - start.mouseX),
            y: start.panelY + (e.clientY - start.mouseY),
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        dragStartRef.current = null;
    }, []);

    const startDrag = useWindowMouseDrag(handleDragMove, handleDragEnd);

    const handleHeaderMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const current = position ?? DEFAULT_POSITION;
            dragStartRef.current = {
                mouseX: e.clientX,
                mouseY: e.clientY,
                panelX: current.x,
                panelY: current.y,
            };
            startDrag(e);
        },
        [position, startDrag]
    );

    if (!open || !position) {
        return null;
    }
    return createPortal(
        <Paper
            elevation={8}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                width,
                zIndex: 1400,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundImage: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <Box sx={{ p: 1.5 }}>
                <PanelHeader
                    title={title}
                    dragListeners={{ onMouseDown: handleHeaderMouseDown }}
                    onClose={onClose}
                />
                <Box sx={{ mt: 1 }}>{children}</Box>
            </Box>
        </Paper>,
        document.body
    );
}
